import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type ReconExpandCommand = 'EXPAND' | 'COLLAPSE';

interface ReconExpandContextValue {
    /** The last header command; a ReconRow acts on it only when `seq` changes after it mounted. */
    command: ReconExpandCommand;
    seq: number;
    expandedCount: number;
    totalCount: number;
    /** A row reports its open state on mount and on every change. */
    report: (id: string, expanded: boolean) => void;
    forget: (id: string) => void;
    toggleAll: () => void;
}

const noop = () => {};

const ReconExpandContext = createContext<ReconExpandContextValue>({
    command: 'EXPAND',
    seq: 0,
    expandedCount: 0,
    totalCount: 0,
    report: noop,
    forget: noop,
    toggleAll: noop,
});

/** Broadcast channel between the header's Expand All pill and the independently-expandable ReconRows. */
export function ReconExpandProvider({ children }: Readonly<{ children: ReactNode }>) {
    const [command, setCommand] = useState<ReconExpandCommand>('EXPAND');
    const [seq, setSeq] = useState(0);
    const [expandedById, setExpandedById] = useState<Record<string, boolean>>({});

    const report = useCallback((id: string, expanded: boolean) => {
        setExpandedById((prev) => (prev[id] === expanded ? prev : { ...prev, [id]: expanded }));
    }, []);

    const forget = useCallback((id: string) => {
        setExpandedById((prev) => {
            if (!(id in prev)) return prev;
            const { [id]: _removed, ...rest } = prev;
            return rest;
        });
    }, []);

    const totalCount = Object.keys(expandedById).length;
    const expandedCount = Object.values(expandedById).filter(Boolean).length;

    const toggleAll = useCallback(() => {
        setCommand(totalCount > 0 && expandedCount === totalCount ? 'COLLAPSE' : 'EXPAND');
        setSeq((prev) => prev + 1);
    }, [expandedCount, totalCount]);

    const value = useMemo(
        () => ({ command, seq, expandedCount, totalCount, report, forget, toggleAll }),
        [command, seq, expandedCount, totalCount, report, forget, toggleAll],
    );
    return <ReconExpandContext.Provider value={value}>{children}</ReconExpandContext.Provider>;
}

export function useReconExpand(): ReconExpandContextValue {
    return useContext(ReconExpandContext);
}
